import type { InternalAxiosRequestConfig } from 'axios';
import { matchRoutes } from 'react-router-dom';
import { API_BASE_URL } from 'config/apiConfig';
import httpClient from 'services/httpClient';
import { sendPerformanceMetrics, PerformanceMetricEvent } from 'services/api/performanceClient';
import { getAuthGeneration } from 'utils/auth';
import endpointTemplates from './performanceEndpointTemplates.json';

const MAX_BUFFER = 100;
const BATCH_SIZE = 50;
const FLUSH_INTERVAL = 15000;
const QUIET_WINDOW = 300;
const SETTLE_TIMEOUT = 15000;

// Generated from Django routes and guarded by a backend drift test. Match the
// most specific templates first; raw URLs never become telemetry labels.
const apiTemplates = endpointTemplates.map((template) => ({
  template,
  parameters: template.split(':param').length - 1,
  pattern: new RegExp(`^${template.split(':param').map((part) => part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('[^/]+')}$`),
})).sort((a, b) => a.parameters - b.parameters);

export const normalizePerformanceRoute = (pathname: string, templates: string[]): string => {
  const matched = matchRoutes(templates.filter((path) => path !== '*').map((path) => ({ path })), pathname);
  return matched?.[matched.length - 1]?.route.path ?? '/unknown';
};

export const normalizePerformanceEndpoint = (url: string): string | null => {
  try {
    const base = new URL(API_BASE_URL, window.location.origin);
    const parsed = new URL(url, `${base.href.replace(/\/$/, '')}/`);
    if (parsed.origin !== base.origin || !parsed.pathname.startsWith('/api/')) return null;
    if (/^\/api\/(?:performance(?:\/|$)|admin\/performance(?:\/|$)|auth(?:\/|$)|token(?:\/|$)|register(?:\/|$))/.test(parsed.pathname)) return null;
    if (parsed.pathname.split('/').includes('stream')) return null;
    return apiTemplates.find(({ pattern }) => pattern.test(parsed.pathname))?.template ?? '/api/unknown/';
  } catch {
    return null;
  }
};

const deviceClass = (): PerformanceMetricEvent['device'] => {
  const width = window.screen?.width || window.innerWidth;
  return width < 768 ? 'mobile' : width < 1024 ? 'tablet' : 'desktop';
};

const eventId = (): string => {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const duration = (end: number, start: number): number | undefined => (
  start > 0 && end >= start && Number.isFinite(end - start) ? end - start : undefined
);

export const resourceMeasurements = (entry: PerformanceResourceTiming) => {
  const serverMs = entry.serverTiming?.find((timing) => timing.name === 'app')?.duration;
  const cached = entry.transferSize === 0 && entry.decodedBodySize > 0;
  return {
  // Zeroed cross-origin fields mean unavailable, not instantaneous.
  dns_ms: duration(entry.domainLookupEnd, entry.domainLookupStart),
  connect_ms: duration(entry.connectEnd, entry.connectStart),
  ttfb_ms: duration(entry.responseStart, entry.requestStart),
  transfer_ms: duration(entry.responseEnd, entry.responseStart),
  server_ms: !cached && serverMs !== undefined && Number.isFinite(serverMs) && serverMs >= 0 && serverMs <= entry.duration + 5 ? serverMs : undefined,
  };
};

export interface PerformanceTracker {
  routeChanged: (route: string) => void;
  flush: () => void;
  stop: () => void;
}

export const startPerformanceTracking = (
  initialRoute: string,
  includeNavigation: boolean,
  isCurrentIdentity: () => boolean,
): PerformanceTracker => {
  const startedAt = performance.now();
  const authGeneration = getAuthGeneration();
  let active = true;
  let route = initialRoute;
  let routeEpoch = 0;
  let routeStarted = startedAt;
  let pendingRequests = 0;
  let settled = false;
  let quietTimer: ReturnType<typeof setTimeout> | undefined;
  let deadlineTimer: ReturnType<typeof setTimeout> | undefined;
  let frame = 0;
  let buffer: PerformanceMetricEvent[] = [];
  let sending = false;
  const abort = new AbortController();
  const observers: PerformanceObserver[] = [];
  const requests = new WeakMap<InternalAxiosRequestConfig, number>();
  const routes = [{ start: startedAt, route: initialRoute }];
  let resourceSamples = 0;
  const current = () => active && isCurrentIdentity() && authGeneration === getAuthGeneration();
  type Sample = Omit<PerformanceMetricEvent, 'event_id' | 'device' | 'route'> & { route?: string };
  const record = (sample: Sample) => {
    if (!current() || !Number.isFinite(sample.value) || sample.value < 0 || sample.value > 3600000) return;
    try {
      buffer.push({ ...sample, route: sample.route ?? route, event_id: eventId(), device: deviceClass() });
      if (buffer.length > MAX_BUFFER) buffer.shift();
    } catch {
      // Missing browser crypto or performance support must not affect the app.
    }
  };
  const flush = () => {
    if (!current()) { buffer = []; return; }
    if (sending || !buffer.length) return;
    const batch = buffer.splice(0, BATCH_SIZE);
    sending = true;
    // No retry queue: failed telemetry is expendable and never persisted locally.
    void sendPerformanceMetrics(batch, abort.signal).catch(() => undefined).finally(() => { sending = false; });
  };
  const observe = (type: string, callback: (entries: PerformanceEntry[]) => void, buffered = false) => {
    if (typeof PerformanceObserver === 'undefined') return;
    try {
      const observer = new PerformanceObserver((list) => {
        if (current()) callback(list.getEntries());
      });
      observer.observe({ type, buffered });
      observers.push(observer);
    } catch {
      // Browsers support different entry types; each metric is independent.
    }
  };
  const cancelSettle = () => {
    clearTimeout(quietTimer);
    clearTimeout(deadlineTimer);
    if (frame) cancelAnimationFrame(frame);
  };
  const scheduleSettle = () => {
    clearTimeout(quietTimer);
    if (frame) cancelAnimationFrame(frame);
    if (settled || pendingRequests || !current()) return;
    const epoch = routeEpoch;
    quietTimer = setTimeout(() => {
      if (typeof requestAnimationFrame !== 'function') return;
      frame = requestAnimationFrame(() => {
        frame = requestAnimationFrame(() => {
          if (!current() || epoch !== routeEpoch || pendingRequests || settled) return;
          settled = true;
          clearTimeout(deadlineTimer);
          record({ kind: 'route', name: 'route_settle', value: performance.now() - routeStarted });
        });
      });
    }, QUIET_WINDOW);
  };
  const startSettle = () => {
    cancelSettle();
    // Background-tab frame throttling is not evidence of a slow application.
    settled = document.visibilityState === 'hidden';
    pendingRequests = 0;
    routeStarted = performance.now();
    if (settled) return;
    deadlineTimer = setTimeout(() => {
      // Keep stalled navigations visible without pretending they completed at
      // the deadline or mixing them into successful-settle percentiles.
      record({ kind: 'route', name: 'route_timeout', value: SETTLE_TIMEOUT });
      settled = true;
      cancelSettle();
    }, SETTLE_TIMEOUT);
    scheduleSettle();
  };
  const requestInterceptor = httpClient.interceptors.request.use((config) => {
    if (current() && normalizePerformanceEndpoint(config.url ?? '') && !settled) {
      requests.set(config, routeEpoch);
      pendingRequests += 1;
      clearTimeout(quietTimer);
      if (frame) cancelAnimationFrame(frame);
    }
    return config;
  }, undefined, { synchronous: true });
  const completed = (config?: InternalAxiosRequestConfig) => {
    if (config && requests.has(config)) {
      const epoch = requests.get(config);
      requests.delete(config);
      if (epoch === routeEpoch) { pendingRequests = Math.max(0, pendingRequests - 1); scheduleSettle(); }
    }
  };
  const responseInterceptor = httpClient.interceptors.response.use((response) => {
    completed(response.config);
    return response;
  }, (error: unknown) => {
    completed((error as { config?: InternalAxiosRequestConfig })?.config);
    return Promise.reject(error);
  });

  observe('resource', (entries) => {
    entries.forEach((raw) => {
      const entry = raw as PerformanceResourceTiming;
      if (!['fetch', 'xmlhttprequest'].includes(entry.initiatorType)) {
        if ((!includeNavigation && entry.startTime < startedAt) || resourceSamples >= 30) return;
        let url: URL;
        try { url = new URL(entry.name); } catch { return; }
        if (![window.location.origin, new URL(API_BASE_URL, window.location.origin).origin].includes(url.origin)) return;
        let name: 'script' | 'stylesheet' | 'image' | 'font' | undefined;
        if (entry.initiatorType === 'script') name = 'script';
        else if (entry.initiatorType === 'img') name = 'image';
        else if (['link', 'css'].includes(entry.initiatorType)) {
          if (/\.(?:woff2?|ttf|otf)$/i.test(url.pathname)) name = 'font';
          else if (/\.css$/i.test(url.pathname)) name = 'stylesheet';
          else if (/\.(?:png|jpe?g|gif|webp|svg|avif)$/i.test(url.pathname)) name = 'image';
        }
        if (!name) return;
        resourceSamples += 1;
        const resourceRoute = [...routes].reverse().find((candidate) => candidate.start <= entry.startTime)?.route ?? initialRoute;
        record({ kind: 'resource', name, route: resourceRoute, value: entry.duration, ...resourceMeasurements(entry) });
        return;
      }
      if (entry.startTime < startedAt) return;
      const endpoint = normalizePerformanceEndpoint(entry.name);
      if (!endpoint) return;
      const entryRoute = [...routes].reverse().find((candidate) => candidate.start <= entry.startTime)?.route;
      if (!entryRoute) return;
      const responseStatus = (entry as PerformanceResourceTiming & { responseStatus?: number }).responseStatus;
      record({ kind: 'api', name: 'request', endpoint, route: entryRoute, value: entry.duration,
        ...resourceMeasurements(entry), status_code: responseStatus && responseStatus >= 100 ? responseStatus : undefined });
    });
  }, includeNavigation);

  let navigationRecorded = false;
  let documentMetrics = includeNavigation;
  let lcp: number | undefined;
  let cls = 0;
  let clsWindow = 0;
  let clsWindowStart = 0;
  let clsLast = 0;
  const recordNavigation = () => {
    if (!documentMetrics || navigationRecorded || typeof performance.getEntriesByType !== 'function') return;
    const entry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    if (!entry?.loadEventEnd) return;
    navigationRecorded = true;
    record({ kind: 'navigation', name: 'page_load', route: initialRoute, value: entry.loadEventEnd - entry.startTime, ...resourceMeasurements(entry) });
    if (entry.responseStart > 0) record({ kind: 'web_vital', name: 'TTFB', route: initialRoute, value: entry.responseStart - entry.startTime });
  };
  const finishDocumentMetrics = () => {
    if (!documentMetrics) return;
    if (lcp !== undefined) record({ kind: 'web_vital', name: 'LCP', route: initialRoute, value: lcp });
    if (typeof PerformanceObserver !== 'undefined' && PerformanceObserver.supportedEntryTypes?.includes('layout-shift')) record({ kind: 'web_vital', name: 'CLS', route: initialRoute, value: cls });
    documentMetrics = false;
  };
  if (includeNavigation) {
    recordNavigation();
    observe('navigation', recordNavigation, true);
    observe('paint', (entries) => {
      if (documentMetrics) entries.filter((entry) => entry.name === 'first-contentful-paint').forEach((entry) => record({ kind: 'web_vital', name: 'FCP', route: initialRoute, value: entry.startTime }));
    }, true);
    observe('largest-contentful-paint', (entries) => { if (documentMetrics) lcp = entries[entries.length - 1]?.startTime ?? lcp; }, true);
    observe('first-input', (entries) => {
      if (documentMetrics && entries[0]) record({ kind: 'web_vital', name: 'FID', route: initialRoute, value: (entries[0] as PerformanceEntry & { processingStart: number }).processingStart - entries[0].startTime });
    }, true);
    observe('layout-shift', (entries) => {
      if (!documentMetrics) return;
      entries.forEach((raw) => {
        const entry = raw as PerformanceEntry & { hadRecentInput: boolean; value: number };
        if (entry.hadRecentInput) return;
        if (entry.startTime - clsLast > 1000 || entry.startTime - clsWindowStart > 5000) { clsWindow = 0; clsWindowStart = entry.startTime; }
        clsWindow += entry.value;
        clsLast = entry.startTime;
        cls = Math.max(cls, clsWindow);
      });
    }, true);
  }
  const hidden = () => {
    if (document.visibilityState === 'hidden') {
      settled = true;
      cancelSettle();
      finishDocumentMetrics();
      flush();
    }
  };
  const pageHide = () => { finishDocumentMetrics(); flush(); };
  document.addEventListener('visibilitychange', hidden);
  window.addEventListener('pagehide', pageHide);
  const interval = setInterval(flush, FLUSH_INTERVAL);
  startSettle();
  return {
    flush,
    routeChanged: (nextRoute) => {
      finishDocumentMetrics();
      route = nextRoute;
      routeEpoch += 1;
      resourceSamples = 0;
      routes.push({ start: performance.now(), route });
      if (routes.length > 30) routes.shift();
      startSettle();
    },
    stop: () => {
      active = false;
      buffer = [];
      abort.abort();
      observers.forEach((observer) => observer.disconnect());
      cancelSettle();
      clearInterval(interval);
      httpClient.interceptors.request.eject(requestInterceptor);
      httpClient.interceptors.response.eject(responseInterceptor);
      document.removeEventListener('visibilitychange', hidden);
      window.removeEventListener('pagehide', pageHide);
    },
  };
};
